/**
 * "Works best for" — Dripify-style 2×2 grid.
 * Each card: icon chip + title + body + "Learn more →" on the left,
 * illustration on the right (files under `public/images/landing/works-best-for/`).
 */

import { motion } from 'framer-motion';
import { ArrowRight, Building2, Rocket, Search, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useB2BView } from '@contexts/B2BViewContext';
import { landingPrimaryCtaMd } from './landingCtaStyles';

/**
 * Base paths (no extension) for works-best-for illustrations.
 * Drop any of .webp / .png / .jpg / .svg into
 *   public/images/landing/works-best-for/
 * with the matching filename — IllustrationSlot tries all formats automatically.
 */
const ICP_ILLUSTRATION = {
  agency:    '/images/landing/works-best-for/recruitment-agencies',
  inHouse:   '/images/landing/works-best-for/in-house-hr',
  smb:       '/images/landing/works-best-for/smb-growth',
  executive: '/images/landing/works-best-for/executive-search',
} as const;

/** Ordered list of extensions to try when loading an illustration. */
const IMG_EXTS = ['.webp', '.png', '.jpg', '.jpeg', '.svg'] as const;

/**
 * Cards ordered: [left-col-top, right-col-top, left-col-bottom, right-col-bottom]
 * Heights alternate tall/short diagonally so the grid feels dynamic, not uniform.
 * minH controls the card's own height — columns are independent flex stacks.
 */
const ICP_CARDS = [
  {
    title: 'Recruitment & Staffing Agencies',
    body: 'Client portal, requisitions, and submittal-ready narratives — with explainable fit your account managers can defend. Full RBAC keeps agency staff, hiring managers, and clients in separate scoped views.',
    icon: Building2,
    illustration: ICP_ILLUSTRATION.agency,
    minH: 'min-h-[320px] lg:min-h-[360px]',
    // Violet — matches the purple interview-panel frames in the illustration
    iconBg: 'bg-violet-500',
    cardBg:
      'bg-gradient-to-br from-violet-50/[0.5] via-white to-white dark:from-violet-950/15 dark:via-stone-950 dark:to-stone-950',
    border: 'border-violet-100/40 dark:border-violet-900/25',
    illustrationBg: 'bg-violet-50/20 dark:bg-violet-950/15',
    illustrationBorder: 'border-violet-200/35 dark:border-violet-800/25',
    accentText: 'text-violet-600 dark:text-violet-400',
    placeholderIcon: 'text-violet-400/55 dark:text-violet-500/45',
    blob: '#8b5cf6',
  },
  {
    title: 'In-house Talent & HR Teams',
    body: 'Employer mode, hiring-manager visibility, and audit-friendly pipelines — without agency clutter.',
    icon: Users,
    illustration: ICP_ILLUSTRATION.inHouse,
    minH: 'min-h-[220px] lg:min-h-[260px]',
    // Rose — matches the bold red @ notification icon in the illustration
    iconBg: 'bg-rose-500',
    cardBg:
      'bg-gradient-to-br from-rose-50/[0.45] via-white to-white dark:from-rose-950/15 dark:via-stone-950 dark:to-stone-950',
    border: 'border-rose-100/40 dark:border-rose-900/25',
    illustrationBg: 'bg-rose-50/20 dark:bg-rose-950/15',
    illustrationBorder: 'border-rose-200/35 dark:border-rose-800/25',
    accentText: 'text-rose-600 dark:text-rose-400',
    placeholderIcon: 'text-rose-400/55 dark:text-rose-500/45',
    blob: '#f43f5e',
  },
  {
    title: 'SMB & Growth-Stage Companies',
    body: "AI interviews and multilingual support when you can't scale headcount — role-fit that cuts screening time.",
    icon: Rocket,
    illustration: ICP_ILLUSTRATION.smb,
    minH: 'min-h-[220px] lg:min-h-[260px]',
    // Amber — matches the orange/amber rocket and person in the illustration
    iconBg: 'bg-amber-500',
    cardBg:
      'bg-gradient-to-br from-amber-50/[0.5] via-white to-white dark:from-amber-950/15 dark:via-stone-950 dark:to-stone-950',
    border: 'border-amber-100/40 dark:border-amber-900/25',
    illustrationBg: 'bg-amber-50/20 dark:bg-amber-950/15',
    illustrationBorder: 'border-amber-200/35 dark:border-amber-800/25',
    accentText: 'text-amber-600 dark:text-amber-400',
    placeholderIcon: 'text-amber-400/55 dark:text-amber-500/45',
    blob: '#f59e0b',
  },
  {
    title: 'Executive Search & Boutique Firms',
    body: 'High-touch shortlists with structured live AI interviews, rubric-scored profiles, and client-safe artifacts — all without enterprise ATS overhead or per-seat pricing traps.',
    icon: Search,
    illustration: ICP_ILLUSTRATION.executive,
    minH: 'min-h-[320px] lg:min-h-[360px]',
    // Blue — matches the professional blue business-suit tones in the illustration
    iconBg: 'bg-blue-600',
    cardBg:
      'bg-gradient-to-br from-blue-50/[0.45] via-white to-white dark:from-blue-950/15 dark:via-stone-950 dark:to-stone-950',
    border: 'border-blue-100/40 dark:border-blue-900/25',
    illustrationBg: 'bg-blue-50/20 dark:bg-blue-950/15',
    illustrationBorder: 'border-blue-200/35 dark:border-blue-800/25',
    accentText: 'text-blue-600 dark:text-blue-400',
    placeholderIcon: 'text-blue-400/55 dark:text-blue-500/45',
    blob: '#2563eb',
  },
] as const;

/**
 * Illustration area.
 * `baseSrc` is an extension-less path. The component tries each entry in
 * IMG_EXTS in order (.webp → .png → .jpg → .jpeg → .svg) until one loads,
 * then falls back to a dashed placeholder if none succeeds.
 */
function IllustrationSlot({
  baseSrc,
  alt,
  bg,
  border,
  iconColor,
  Icon,
}: {
  baseSrc: string;
  alt: string;
  bg: string;
  border: string;
  iconColor: string;
  Icon: React.ElementType;
}) {
  const [extIdx, setExtIdx] = useState(0);
  useEffect(() => { setExtIdx(0); }, [baseSrc]);

  const allExhausted = extIdx >= IMG_EXTS.length;
  const currentSrc   = allExhausted ? '' : `${baseSrc}${IMG_EXTS[extIdx]}`;

  if (!allExhausted) {
    return (
      <div className="flex h-full w-full overflow-hidden">
        <img
          key={currentSrc}
          src={currentSrc}
          alt={alt}
          className="h-full w-full object-cover object-center"
          draggable={false}
          loading="lazy"
          decoding="async"
          onError={() => setExtIdx((i) => i + 1)}
        />
      </div>
    );
  }

  return (
    <div
      className={`flex h-full w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed ${bg} ${border}`}
    >
      <Icon className={`h-12 w-12 ${iconColor}`} strokeWidth={1.25} aria-hidden />
      <span className={`max-w-[90%] text-center text-[8px] font-semibold uppercase leading-tight tracking-[0.14em] ${iconColor}`}>
        Add illustration
        <br />
        <span className="font-mono text-[7px] font-normal normal-case tracking-normal opacity-70">
          {baseSrc}.(webp|png|jpg|svg)
        </span>
      </span>
    </div>
  );
}

type IcpCard = (typeof ICP_CARDS)[number];

function CardContent({ card, Icon }: { card: IcpCard; Icon: React.ElementType }) {
  return (
    <>
      {/* Text side */}
      <div className={`relative z-10 flex h-full flex-col p-6 pr-[57%] md:p-7 md:pr-[57%] ${card.minH}`}>
        <div
          className={`mb-4 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${card.iconBg} shadow-sm`}
        >
          <Icon className="h-5 w-5 text-white" strokeWidth={2} aria-hidden />
        </div>
        <h3 className="text-lg font-bold leading-snug text-gray-900 dark:text-white md:text-xl">
          {card.title}
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-400 md:text-[15px]">
          {card.body}
        </p>
        <Link
          to="/business/demo"
          className={`mt-auto inline-flex items-center gap-1 pt-4 text-sm font-semibold transition-opacity hover:opacity-80 ${card.accentText}`}
        >
          Learn more
          <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
      </div>

      {/* Illustration strip (right 54%) — full-bleed, card overflow-hidden clips the corners */}
      <div className="absolute bottom-0 right-0 top-0 w-[54%]">
        <IllustrationSlot
          baseSrc={card.illustration}
          alt={`${card.title} illustration`}
          bg={card.illustrationBg}
          border={card.illustrationBorder}
          iconColor={card.placeholderIcon}
          Icon={Icon}
        />
      </div>

      {/* Glow blob */}
      <div
        className="pointer-events-none absolute -bottom-8 -right-6 h-36 w-36 rounded-full opacity-[0.07] blur-2xl"
        style={{ background: card.blob }}
        aria-hidden
      />
    </>
  );
}

export function B2BWorksBestFor() {
  const { surface } = useB2BView();

  return (
    <section
      id="who-its-for"
      data-b2b-surface={surface}
      className="scroll-mt-28 relative overflow-hidden bg-transparent pt-12 pb-12 md:pt-16 md:pb-16"
    >
      <div className="mx-auto w-full max-w-[1600px] px-4 sm:px-8 md:px-10 lg:px-12 xl:px-16">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-8 text-center md:mb-10"
        >
          <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.25em] text-[#8E86FF] dark:text-violet-400">
            Solutions
          </p>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white sm:text-3xl md:text-4xl">
            Works best for
          </h2>
          <p className="mx-auto mt-2 max-w-2xl text-base text-gray-500 dark:text-gray-400 md:text-lg">
            Whether you run a consultancy or an internal HR team, the same core — live AI interviews,
            multilingual experiences, and RBAC — adapts to your mode.
          </p>
        </motion.div>

        {/*
          Two independent flex columns — cards are NOT in a shared grid row,
          so each card keeps its own height (tall / short alternates diagonally).
          On mobile: single column, all 4 stacked in order.
        */}
        <div className="flex flex-col gap-5 sm:flex-row sm:items-start md:gap-6">
          {/* Left column: card[0] tall + card[2] short */}
          <div className="flex flex-1 flex-col gap-5 md:gap-6">
            {[ICP_CARDS[0], ICP_CARDS[2]].map((card, colIdx) => {
              const Icon = card.icon;
              const delay = colIdx === 0 ? 0 : 0.14;
              return (
                <motion.article
                  key={card.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay }}
                  className={`group relative overflow-hidden rounded-[1.75rem] border shadow-sm transition-shadow duration-300 hover:shadow-md md:rounded-[2rem] ${card.minH} ${card.cardBg} ${card.border}`}
                >
                  <CardContent card={card} Icon={Icon} />
                </motion.article>
              );
            })}
          </div>

          {/* Right column: card[1] short + card[3] tall */}
          <div className="flex flex-1 flex-col gap-5 md:gap-6">
            {[ICP_CARDS[1], ICP_CARDS[3]].map((card, colIdx) => {
              const Icon = card.icon;
              const delay = colIdx === 0 ? 0.07 : 0.21;
              return (
                <motion.article
                  key={card.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay }}
                  className={`group relative overflow-hidden rounded-[1.75rem] border shadow-sm transition-shadow duration-300 hover:shadow-md md:rounded-[2rem] ${card.minH} ${card.cardBg} ${card.border}`}
                >
                  <CardContent card={card} Icon={Icon} />
                </motion.article>
              );
            })}
          </div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-10 flex justify-center md:mt-12"
        >
          <Link
            to="/business/demo"
            className={landingPrimaryCtaMd}
          >
            Request a demo
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
